import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps, addressFromComponents } from "../lib/google_maps"
import { FreehandPen, closesTheGap } from "../lib/area_drawing"
import { track } from "../lib/mixpanel"

export default class extends Controller {
  static targets = ["codeField", "nameField", "addressField", "cityField", "stateField", "zipcodeField", "timezoneField", "submitBtn", "errors",
    "propertyFields", "propertyNote", "search", "map", "mapWrap", "mapNote", "coordChip", "satBtn", "mapBtn",
    "areaSection", "areaMode", "areaSize", "areaDrawTools", "drawTools", "drawToggle", "drawHint", "undoBtn", "eraseBtn"]
  static values = { updateUrl: String, hasProperty: Boolean, lat: Number, lng: Number, area: Object }

  // Called by change-property controller after a property switch
  updateFields({ code, name, address, city, state, zipcode }) {
    this.codeFieldTarget.value = code || ""
    this.nameFieldTarget.value = name || ""
    this.addressFieldTarget.value = address || ""
    this.cityFieldTarget.value = city || ""
    this.stateFieldTarget.value = state || ""
    this.zipcodeFieldTarget.value = zipcode || ""
    this.hasPropertyValue = true
    if (this.hasPropertyFieldsTarget) this.propertyFieldsTarget.classList.remove("hidden")
    if (this.hasPropertyNoteTarget) this.propertyNoteTarget.classList.remove("hidden")
  }

  // ── Map pin (Larry K3-20) ─────────────────────────────────────────
  // The map is built the first time the pop-up opens (a map drawn inside a
  // hidden box comes out grey). Search, a click on the map or dragging the
  // pin set the spot and fill the address fields from it.
  opened() {
    if (this._mapStarted) {
      if (this._map) google.maps.event.trigger(this._map, "resize")
      return
    }
    this._mapStarted = true
    loadGoogleMaps()
      .then(() => this._initMap())
      .catch(() => {
        if (this.hasMapWrapTarget) this.mapWrapTarget.classList.add("hidden")
        if (this.hasMapNoteTarget) this.mapNoteTarget.classList.remove("hidden")
      })
  }

  showSatellite() { this._setMapType("hybrid") }
  showRoadmap() { this._setMapType("roadmap") }

  _setMapType(type) {
    if (this._map) this._map.setMapTypeId(type)
    ;[[this.hasSatBtnTarget && this.satBtnTarget, "hybrid"], [this.hasMapBtnTarget && this.mapBtnTarget, "roadmap"]].forEach(([btn, t]) => {
      if (!btn) return
      btn.classList.toggle("bg-brand", t === type)
      btn.classList.toggle("text-white", t === type)
    })
  }

  _initMap() {
    const hasStart = Number.isFinite(this.latValue) && this.latValue !== 0
    const start = hasStart ? { lat: this.latValue, lng: this.lngValue } : { lat: 39.5, lng: -98.35 }
    this._map = new google.maps.Map(this.mapTarget, {
      center: start, zoom: hasStart ? 16 : 4, mapTypeId: "hybrid",
      streetViewControl: false, mapTypeControl: false, fullscreenControl: false, gestureHandling: "greedy"
    })
    this._setMapType("hybrid")
    this._marker = new google.maps.Marker({ map: this._map, draggable: true, visible: hasStart })
    if (hasStart) { this._marker.setPosition(start); this._showCoords(start.lat, start.lng) }
    this._geocoder = new google.maps.Geocoder()

    this._marker.addListener("dragend", () => {
      const p = this._marker.getPosition()
      this._pinAt(p.lat(), p.lng(), { fill: true })
    })
    this._map.addListener("click", (ev) => {
      // Drawing the job's time clock area (K3-25) with the pencil on: a click is a corner, not the pin.
      if (this.hasAreaSectionTarget && this._areaMode() === "drawn" && this._drawing) return this._addCorner(ev.latLng)
      this._pinAt(ev.latLng.lat(), ev.latLng.lng(), { fill: true })
    })

    if (this.hasSearchTarget) {
      this._autocomplete = new google.maps.places.Autocomplete(this.searchTarget, {
        fields: ["address_components", "formatted_address", "geometry", "name"]
      })
      this._autocomplete.addListener("place_changed", () => {
        const place = this._autocomplete.getPlace()
        if (!place?.geometry) return
        this._fillAddress(addressFromComponents(place.address_components, place.formatted_address))
        this._pinAt(place.geometry.location.lat(), place.geometry.location.lng(), { recenter: true })
      })
      // Enter picks the suggestion; it must not reach anything else on the page.
      this.searchTarget.addEventListener("keydown", (e) => { if (e.key === "Enter") e.preventDefault() })
    }

    this._initArea()

    // No pin yet: center on the address already typed, without placing one.
    if (!hasStart) {
      const typed = [this.addressFieldTarget.value, this.cityFieldTarget.value, this.stateFieldTarget.value, this.zipcodeFieldTarget.value]
        .map((v) => v.trim()).filter(Boolean).join(", ")
      if (typed) {
        this._geocoder.geocode({ address: typed }, (results, status) => {
          if (status === "OK" && results[0]) { this._map.setCenter(results[0].geometry.location); this._map.setZoom(16) }
        })
      }
    }
  }

  _pinAt(lat, lng, { recenter = false, fill = false } = {}) {
    this._pin = { lat: Number(lat.toFixed(7)), lng: Number(lng.toFixed(7)) }
    this._marker.setPosition({ lat, lng })
    this._marker.setVisible(true)
    if (recenter) { this._map.setCenter({ lat, lng }); this._map.setZoom(18) }
    this._showCoords(lat, lng)
    this._drawArea()
    if (fill) {
      this._geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) this._fillAddress(addressFromComponents(results[0].address_components, results[0].formatted_address))
      })
    }
  }

  // ── The job's time clock area (Kloepfer K3-25) ────────────────────
  // Its own size, as a circle around the pin, or an outline drawn corner by
  // corner (the company's size went with K13-9: every job has its own). Sent
  // with Save only when changed.
  _initArea() {
    if (!this.hasAreaSectionTarget) return
    const shape = this.areaValue?.shape || []
    this._corners = new google.maps.MVCArray(shape.map(([lat, lng]) => new google.maps.LatLng(lat, lng)))
    ;["insert_at", "set_at", "remove_at"].forEach((ev) => this._corners.addListener(ev, () => { this._areaDirty = true; this._paintShape() }))
    this._history = []
    this._circle = new google.maps.Circle({ map: this._map, clickable: false, strokeWeight: 2 })
    // The corners go in as the one path of an explicit list of paths: an empty
    // MVCArray given straight to `paths` is taken for the list itself (Maps 3.66).
    this._polygon = new google.maps.Polygon({ map: this._map, paths: new google.maps.MVCArray([ this._corners ]), editable: true,
      strokeColor: "#2563eb", strokeWeight: 2, fillColor: "#2563eb", fillOpacity: 0.15 })
    // The pencil (Phelipe 2026-10-09) on the map's bottom-right corner turns
    // drawing on and off while "Draw it on the map" is chosen.
    const slot = google.maps.ControlPosition?.RIGHT_BOTTOM
    const tools = this.hasDrawToolsTarget ? this.drawToolsTarget : (this.hasDrawToggleTarget ? this.drawToggleTarget : null)
    if (tools && slot !== undefined && this._map.controls?.[slot]) this._map.controls[slot].push(tools)
    // Pencil on: draw around the job with the mouse held down (or tap corners).
    this._pen = new FreehandPen({ container: this.mapTarget, map: this._map,
      onTap: (latLng) => this._addCorner(latLng), onStroke: (points) => this._setCorners(points) })
    // Opening the box: the pencil starts off, so the pin and the map move as usual
    // until they click it (Phelipe 2026-10-09), and a drawn area shows whole.
    this._setDrawing(false)
    this._drawArea()
    this._frameDrawing()
  }

  // Zoom the map out to the drawn area, with room around it.
  _frameDrawing() {
    if (!this._corners || this._corners.getLength() < 3 || !google.maps.LatLngBounds || !this._map?.fitBounds) return
    const bounds = new google.maps.LatLngBounds()
    this._corners.getArray().forEach((p) => bounds.extend(p))
    this._map.fitBounds(bounds, 48)
  }

  _areaMode() {
    return this.areaModeTargets.find((radio) => radio.checked)?.value || "size"
  }

  // The size box, in the company's feet or meters, as meters.
  _areaMeters() {
    const n = Number(this.hasAreaSizeTarget ? this.areaSizeTarget.value : NaN)
    if (!Number.isFinite(n) || n <= 0) return null
    return this.areaValue?.unit === "m" ? n : n / 3.28084
  }

  _drawArea() {
    if (!this.hasAreaSectionTarget) return
    const mode = this._areaMode()
    if (this.hasAreaDrawToolsTarget) this.areaDrawToolsTarget.classList.toggle("hidden", mode !== "drawn")
    if (this.hasDrawToolsTarget) this.drawToolsTarget.classList.toggle("hidden", mode !== "drawn")
    else if (this.hasDrawToggleTarget) this.drawToggleTarget.classList.toggle("hidden", mode !== "drawn")
    this._paintShape()
    if (!this._circle) return
    const center = this._marker?.getVisible() ? this._marker.getPosition() : null
    const radius = mode === "size" ? this._areaMeters() : this.areaValue?.company_radius_m
    const color = mode === "company" ? "#6b7280" : "#2563eb"
    this._circle.setOptions({ strokeColor: color, fillColor: color, fillOpacity: mode === "company" ? 0.08 : 0.15 })
    this._circle.setVisible(mode !== "drawn" && !!center && !!radius)
    if (center && radius) { this._circle.setCenter(center); this._circle.setRadius(radius) }
    this._polygon.setVisible(mode === "drawn")
  }

  areaModeChanged() {
    this._areaDirty = true
    // The pen comes on for a fresh drawing; an outline already there starts with it off.
    this._setDrawing(this._areaMode() === "drawn" && (!this._corners || this._corners.getLength() < 3))
    this._drawArea()
  }

  // The pencil: on, drawing with the mouse held down makes the outline and a
  // tap adds a corner; off, corners can be dragged, a click moves the pin and
  // the map pans freely while the drawing stays.
  toggleDrawing() {
    this._setDrawing(!this._drawing)
  }

  // A stroke replaces the outline (Undo brings the old one back).
  _setCorners(points, { remember = true } = {}) {
    if (!this._corners) return
    if (remember) this._remember()
    this._corners.clear()
    points.slice(0, 40).forEach((p) => this._corners.push(new google.maps.LatLng(p.lat, p.lng)))
    // A released stroke is a finished drawing (an Undo only restores the corners).
    if (remember && this._corners.getLength() >= 3) this._finishDrawing()
  }

  _setDrawing(on) {
    this._drawing = !!on
    if (this.hasDrawToggleTarget) {
      const b = this.drawToggleTarget
      b.setAttribute("aria-pressed", String(this._drawing))
      b.classList.toggle("bg-brand", this._drawing)
      b.classList.toggle("text-white", this._drawing)
      b.classList.toggle("bg-white", !this._drawing)
      b.classList.toggle("text-gray-600", !this._drawing)
      b.title = (this._drawing ? b.dataset.titleOn : b.dataset.titleOff) || ""
    }
    if (this.hasDrawHintTarget) this.drawHintTarget.textContent = (this._drawing ? this.drawHintTarget.dataset.on : this.drawHintTarget.dataset.off) || ""
    this._pen?.enable(this._drawing)
    this._polygon?.setOptions?.({ clickable: !this._drawing })
    this._map?.setOptions?.({ draggableCursor: this._drawing ? "crosshair" : null })
    this._paintShape()
  }

  areaSizeChanged() {
    this._areaDirty = true
    this._drawArea()
  }

  _addCorner(latLng) {
    if (!this._corners || this._corners.getLength() >= 40) return
    // Back on the first corner: the loop is closed, the drawing is done.
    if (closesTheGap(this._corners.getArray(), latLng, this._map?.getZoom?.())) return this._finishDrawing()
    this._remember()
    this._corners.push(latLng)
  }

  // Done drawing (the loop closed, or a stroke released): the pencil goes off,
  // the area lights up, and the corners can be dragged.
  _finishDrawing() {
    this._setDrawing(false)
    this._paintShape()
  }

  // Undo goes back one step: the last stroke, or the last corner.
  undoCorner() {
    if (!this._corners) return
    const before = this._history?.pop()
    if (before) this._setCorners(before, { remember: false })
    else if (this._corners.getLength()) this._corners.pop()
  }

  clearCorners() {
    if (!this._corners?.getLength()) return
    this._remember()
    this._corners.clear()
  }

  _remember() {
    if (!this._history) this._history = []
    this._history.push(this._corners.getArray().map((p) => ({ lat: p.lat(), lng: p.lng() })))
    if (this._history.length > 50) this._history.shift()
  }

  // Filled once the outline closes (3+ corners), stronger once the drawing is
  // done (pencil off), stroke only before; Undo and Start over light up when
  // there's something to take back.
  _paintShape() {
    const n = this._corners ? this._corners.getLength() : 0
    const fill = n < 3 ? 0 : (this._drawing ? 0.2 : 0.35)
    this._polygon?.setOptions?.({ fillOpacity: fill, strokeWeight: n >= 3 ? 3 : 2 })
    if (this.hasUndoBtnTarget) this.undoBtnTarget.disabled = n === 0 && !(this._history?.length)
    if (this.hasEraseBtnTarget) this.eraseBtnTarget.disabled = n === 0
  }

  _areaPayload() {
    if (!this.hasAreaSectionTarget || !this._areaDirty) return null
    const mode = this._areaMode()
    if (mode === "size") return { mode, radius_m: Math.round(this._areaMeters() || 0) }
    if (mode === "drawn") {
      const corners = this._corners ? this._corners.getArray() : []
      return { mode, shape: corners.map((p) => [Number(p.lat().toFixed(7)), Number(p.lng().toFixed(7))]) }
    }
    return null
  }

  _fillAddress(parts) {
    if (!parts) return
    if (parts.address) this.addressFieldTarget.value = parts.address
    if (parts.city) this.cityFieldTarget.value = parts.city
    this.stateFieldTarget.value = parts.state
    this.zipcodeFieldTarget.value = parts.zipcode
  }

  _showCoords(lat, lng) {
    if (!this.hasCoordChipTarget) return
    this.coordChipTarget.textContent = `${lat.toFixed(5)}, ${lng.toFixed(5)}`
    this.coordChipTarget.classList.remove("hidden")
  }

  async save() {
    const code = this.codeFieldTarget.value.trim()
    const name = this.nameFieldTarget.value.trim()
    const address = this.addressFieldTarget.value.trim()
    const city = this.cityFieldTarget.value.trim()
    const state = this.stateFieldTarget.value.trim()

    const errors = []
    if (this.hasPropertyValue && !code) errors.push("Property # is required")
    if (this.hasPropertyValue && !name) errors.push("Property Name is required")
    if (!address) errors.push("Address is required")
    if (!city) errors.push("City is required")
    // No state check here: only a US property needs one, and the save knows
    // the property's country (Luma's UK and European sites, 2026-10-06).

    if (errors.length > 0) {
      this._showErrors(errors)
      return
    }

    this._hideErrors()
    const jobArea = this._areaPayload()
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = "Saving..."

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          code,
          name,
          address,
          city,
          state,
          zipcode: this.zipcodeFieldTarget.value.trim(),
          time_zone: this.hasTimezoneFieldTarget ? this.timezoneFieldTarget.value : null,
          // Only a pin set in this visit is sent, so saving never moves it.
          ...(this._pin ? { latitude: this._pin.lat, longitude: this._pin.lng } : {}),
          ...(jobArea ? { job_area: jobArea } : {})
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        const card = document.getElementById("location-card-body")
        if (card) {
          this._updateField(card, "property", data.property)
          this._updateField(card, "address", data.full_address)
        }

        if (data.project_name) this._updateProjectName(data.project_name)
        if (jobArea) {
          track("Job Area Set", { project_id: data.project_id, mode: jobArea.mode })
          this._areaDirty = false
        }
        if (this._pin) {
          track("Project Location Pinned", { project_id: data.project_id, has_property: this.hasPropertyValue })
          this.latValue = this._pin.lat
          this.lngValue = this._pin.lng
          this._pin = null
        }

        // Ready for the next visit: reopening it without a page reload used to
        // find Save stuck on "Saving...".
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = "Save Changes"
        this._closeModal()
        this._showFlash("Location updated successfully.")
      } else {
        this._showErrors(data.errors || ["Update failed"])
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = "Save Changes"
      }
    } catch {
      this._showErrors(["A network error occurred. Please try again."])
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = "Save Changes"
    }
  }

  _updateField(container, fieldName, value) {
    const el = container.querySelector(`[data-field="${fieldName}"]`)
    if (!el) return
    const label = el.querySelector("span")
    if (label) {
      el.textContent = ""
      el.appendChild(label)
      el.append(` ${value || "—"}`)
    } else {
      el.textContent = value || "—"
    }
  }

  _showErrors(messages) {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.innerHTML = messages.map(m => `<p>${m}</p>`).join("")
    this.errorsTarget.classList.remove("hidden")
  }

  _hideErrors() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.classList.add("hidden")
    this.errorsTarget.innerHTML = ""
  }

  _updateProjectName(name) {
    const header = document.querySelector("[data-project-name]")
    if (header) header.textContent = name

    const breadcrumb = document.querySelector("[data-breadcrumb-project-name]")
    if (breadcrumb) breadcrumb.textContent = name.length > 40 ? name.substring(0, 37) + "..." : name

    document.title = name
  }

  _closeModal() {
    this.element.classList.add("hidden")
    document.body.style.overflow = ""
  }

  _showFlash(message) {
    const container = document.createElement("div")
    container.className = "flash-toast-notice"
    container.dataset.controller = "flash"
    container.dataset.flashDismissAfterValue = "5000"
    container.setAttribute("role", "alert")

    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>`

    const msgEl = document.createElement("p")
    msgEl.className = "flex-1 text-sm"
    msgEl.textContent = message

    container.appendChild(iconDiv)
    container.appendChild(msgEl)
    document.body.insertBefore(container, document.body.firstChild)
  }
}
